/**
 * Custom Apps Grid
 *
 * Renders the user's Custom Applications using the shared SortableAppGrid
 * (dnd-kit drag-to-reorder), with edit/delete buttons overlaid on hover.
 */

import React from "react";
import { Pencil, Trash2 } from "lucide-react";
import SortableAppGrid from "./SortableAppGrid";
import type { App, UserCustomApp } from "../../types";

export const customAppToApp = (custom: UserCustomApp): App => ({
  id: custom.id,
  appName: custom.name,
  appDescription: custom.description || "",
  appIcon: "layout-grid",
  appUrl: custom.url,
  appColor: custom.color || "#c00035",
  appImageUrl: custom.imageUrl ?? undefined,
  isActive: true,
  displayOrder: custom.displayOrder,
  isFavorite: custom.isFavorite ?? false,
});

interface CustomAppsGridProps {
  apps: UserCustomApp[];
  sortable: boolean;
  onReorder: (activeId: number, overId: number) => void;
  onEdit: (app: UserCustomApp) => void;
  onDelete: (app: UserCustomApp) => void;
}

const CustomAppsGrid: React.FC<CustomAppsGridProps> = ({
  apps,
  sortable,
  onReorder,
  onEdit,
  onDelete,
}) => {
  const byId = new Map(apps.map((item) => [item.id, item]));

  return (
    <SortableAppGrid
      apps={apps.map(customAppToApp)}
      sortable={sortable}
      onReorder={onReorder}
      isUserCustomApp
      renderOverlay={(app) => {
        const item = byId.get(app.id);
        if (!item) return null;
        return (
          <div className="absolute top-2.5 right-2.5 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity z-20">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="p-1.5 rounded-md bg-white/95 shadow-sm border border-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
              title="Edit App"
            >
              <Pencil size={12} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              className="p-1.5 rounded-md bg-white/95 shadow-sm border border-gray-200 text-gray-500 hover:text-red-600 transition-colors"
              title="Delete App"
            >
              <Trash2 size={12} />
            </button>
          </div>
        );
      }}
    />
  );
};

export default CustomAppsGrid;
